<script setup>
import LanguageSwitcher from "@/Components/LanguageSwitch/LanguageSwitcher.vue";
import NavButton from "@/Components/Navigation/NavButton.vue";
import NavDesktop from "@/Components/Navigation/NavDesktop.vue";
import NavMobile from "@/Components/Navigation/NavMobile.vue";
import NavSubLink from "@/Components/Navigation/NavSubLink.vue";
import { Head, Link, usePage } from "@inertiajs/vue3";
import { computed, onMounted, useSlots } from "vue";
import {
    ArrowRightStartOnRectangleIcon as LogoutIcon,
    ClipboardDocumentListIcon,
    PhoneIcon,
    UserIcon,
} from "@heroicons/vue/24/outline/index.js";
import ConfirmationModal from "@/Layouts/ConfirmationModal.vue";

const props = defineProps({
    title: String,
});

const slots = useSlots();

const page = usePage();

onMounted(() => {
    document.documentElement.style.setProperty("--primary-color", page.props.colors.primary);
    document.documentElement.style.setProperty("--primary-light-color", page.props.colors.primaryLight);
    document.documentElement.style.setProperty("--primary-dark-color", page.props.colors.primaryDark);
});

const pageTitle = computed(() => {
    return `${props.title} - ${page.props.currentTenant.name}`;
});

const canShowAuthenticatedNavigation = computed(() => {
    const user = page.props.auth.user;

    return (
        user && (user.two_factor_confirmed_at || page.props.twoFactorRequired === false || page.props.isImpersonated)
    );
});

const flashMessages = computed(() =>
    [
        {
            message: page.props.flash.error,
            classes: "border-red-200 bg-red-50 text-red-700",
        },
        {
            message: page.props.flash.success,
            classes: "border-green-200 bg-green-50 text-green-700",
        },
        {
            message: page.props.flash.notice,
            classes: "border-blue-200 bg-blue-50 text-blue-700",
        },
    ].filter(({ message }) => message),
);
</script>

<template>
    <Head :title="pageTitle" />

    <div id="all_content" class="text-gray-700 min-h-screen box-border pt-[72px]">
        <header class="fixed w-full z-40 top-0 left-0 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm">
            <div class="h-1 bg-gradient-to-r from-theme-primary via-theme-primary-light to-theme-primary-dark" />

            <div class="h-[68px] max-w-6xl lg:container mx-auto w-full px-4 sm:px-6">
                <div class="h-full grid grid-cols-[auto_1fr_auto] items-center gap-4">
                    <div class="flex items-center gap-3 min-w-0">
                        <NavMobile />

                        <Link :href="route('dashboard')" class="group flex h-full min-w-0 items-center">
                            <img
                                v-if="$page.props.currentTenant.logo_path"
                                :src="$page.props.currentTenant.logo_path"
                                :alt="$page.props.currentTenant.name"
                                class="block h-10 max-h-10 w-auto max-w-[190px] sm:max-w-[230px] object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                            />
                            <span
                                v-else
                                class="text-lg sm:text-xl font-black tracking-tight text-gray-900 group-hover:text-theme-primary transition-colors"
                            >
                                {{ $page.props.currentTenant.name }}
                            </span>
                        </Link>
                    </div>

                    <div class="hidden xl:flex justify-center min-w-0">
                        <NavDesktop />
                    </div>

                    <div class="flex items-center justify-end gap-2 sm:gap-3">
                        <ul v-if="canShowAuthenticatedNavigation" class="hidden xl:flex items-center gap-2">
                            <li v-if="$page.props.permissions.view_invoice_requests" class="h-[68px] flex items-center">
                                <Link
                                    :href="route('invoice-requests.index')"
                                    class="flex h-10 gap-2 px-4 items-center rounded-xl font-bold text-sm transition-all duration-300 bg-theme-primary text-white hover:-translate-y-0.5 hover:brightness-95"
                                    :class="
                                        route().current('invoice-requests.*')
                                            ? 'ring-2 ring-theme-primary ring-offset-2'
                                            : ''
                                    "
                                >
                                    <ClipboardDocumentListIcon class="size-5" />
                                    {{ trans.get("navigation.invoice-requests") }}
                                </Link>
                            </li>

                            <NavButton :active="route().current('profile.*')">
                                <template #menu>
                                    <UserIcon class="size-5" />
                                    <span class="sr-only">{{ trans.get("navigation.account") }}</span>
                                </template>

                                <template #dropdown>
                                    <ul class="space-y-1">
                                        <NavSubLink
                                            :href="route('profile.show')"
                                            :active="route().current('profile.*')"
                                        >
                                            <UserIcon class="nav-icon" />
                                            {{ trans.get("navigation.profile") }}
                                        </NavSubLink>
                                        <NavSubLink
                                            v-if="$page.props.isImpersonated"
                                            :href="route('logout.impersonation')"
                                            method="post"
                                        >
                                            <LogoutIcon class="nav-icon" />
                                            {{ trans.get("navigation.logout") }}
                                        </NavSubLink>
                                        <NavSubLink v-else :href="route('logout')" method="post">
                                            <LogoutIcon class="nav-icon" />
                                            {{ trans.get("navigation.logout") }}
                                        </NavSubLink>
                                    </ul>
                                </template>
                            </NavButton>
                        </ul>

                        <span v-if="$page.props.auth.user === null" class="h-[68px] flex items-center">
                            <Link
                                :href="route('login')"
                                class="flex h-10 gap-2 px-4 items-center rounded-xl font-bold text-sm transition-all duration-300 bg-theme-primary text-white hover:-translate-y-0.5 hover:brightness-95"
                            >
                                <UserIcon class="size-5" />
                                {{ trans.get("navigation.login") }}
                            </Link>
                        </span>

                        <div class="group shrink-0 relative hidden h-[68px] xl:flex items-center">
                            <a
                                :href="'tel:' + page.props.currentTenant.phone"
                                class="size-10 rounded-xl border border-gray-200 bg-white text-gray-500 flex items-center justify-center hover:border-theme-primary hover:text-theme-primary transition-all duration-300"
                            >
                                <span class="sr-only">{{ page.props.currentTenant.phone }}</span>
                                <PhoneIcon class="size-5" />
                            </a>
                        </div>

                        <LanguageSwitcher />
                    </div>
                </div>
            </div>
        </header>

        <slot name="banner"></slot>

        <!-- Page Content -->
        <main class="max-w-5xl lg:container mx-auto py-8 2xl:py-12 px-4" :class="slots.banner ? '' : 'mt-16'">
            <div
                v-for="flashMessage in flashMessages"
                :key="flashMessage.message"
                class="mb-6 rounded-xl border px-4 py-3 text-sm font-medium"
                :class="flashMessage.classes"
            >
                {{ flashMessage.message }}
            </div>

            <slot />
        </main>
    </div>
    <ConfirmationModal />
</template>
